import { Menu, Moon, Search, Sun } from "lucide-react";
import { Link } from "react-router-dom";

import { EunerdMark } from "@/components/eunerd-mark";
import { NotificationsBell } from "@/components/notifications-bell";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/user-menu";

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { theme, setTheme } = useTheme();
  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const openCmdK = () => {
    const event = new KeyboardEvent("keydown", {
      key: "k",
      metaKey: true,
      ctrlKey: true,
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  return (
    <header className="sticky top-0 z-docked border-b border-slate-200/60 dark:border-white/5 bg-white/80 dark:bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6">
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onMenuClick}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2" aria-label="Ir para dashboard">
            <EunerdMark className="h-8 w-auto" />
            <span className="font-bold tracking-tight text-foreground hidden sm:inline">op-next</span>
          </Link>
        </div>

        {/* Search trigger pra Cmd+K */}
        <button
          type="button"
          onClick={openCmdK}
          className="
            flex-1 max-w-md hidden md:flex items-center gap-2
            rounded-xl border border-slate-200/60 dark:border-white/10
            bg-slate-50/60 hover:bg-slate-100 dark:bg-white/[0.02] dark:hover:bg-white/5
            px-3 py-1.5 text-sm text-muted-foreground
            transition-colors text-left
          "
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1">Buscar (chamados, clientes, nerds...)</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 rounded-md border border-slate-200/80 dark:border-white/10 bg-white dark:bg-card px-1.5 py-0.5 text-[10px] font-mono">
            ⌘K
          </kbd>
        </button>

        <div className="flex items-center gap-1">
          {/* Mobile: search icon → abre Cmd+K */}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={openCmdK} aria-label="Buscar">
            <Search className="h-5 w-5" />
          </Button>
          <NotificationsBell />
          <Button variant="ghost" size="icon" aria-label="Alternar tema" onClick={toggleTheme}>
            <Sun className="h-5 w-5 dark:hidden" aria-hidden />
            <Moon className="h-5 w-5 hidden dark:block" aria-hidden />
          </Button>
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
